import { Stack } from "@mui/material";
import { useState } from "react";
import { useAuthContext } from "../auth/AuthContext";
import Actions from "../components/actions/Actions";
import AddFab from "../components/actions/AddFab";
import ConfirmationDialog from "../components/confirmationDialog/ConfirmationDialog";
import type { Image } from "../components/imagesPreview/ImagePreview";
import ImagesPreview from "../components/imagesPreview/ImagesPreview";
import { AddImageDialog } from "./AddImageDialog";
import CropImageDialog from "./CropImageDialog";
import DownloadImages from "./DownloadImages";
import EditImageDialog from "./EditImageDialog";
import type { CropArea, MediaImage } from "./mediaApi";
import { useFilteredAndOrderedImages } from "./useFilteredAndOrderedImages";
import { useMediaSearch } from "./useMediaSearch";

const MediaBank = () => {
  const { context } = useAuthContext();
  const { searchCriteria, setSearchCriteria } = useMediaSearch();
  const { images, addImage, updateImage, cropImage, deleteImage, isLoading } = useFilteredAndOrderedImages(
    searchCriteria.text,
  );
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<string | null>(null);
  const [imageToEdit, setImageToEdit] = useState<Image | null>(null);
  const [imageToCrop, setImageToCrop] = useState<Image | null>(null);
  const [selectedImages, setSelectedImages] = useState<MediaImage[] | null>(null);

  const updateImagePrivacy = async (image: Image, isPrivate: boolean) => {
    await updateImage(image.id, {
      name: image.name,
      tags: image.tags,
      products: image.products,
      info: image.info,
      source: image.source,
      isPrivate,
    });
  };

  const handleNewFile = async (formData: FormData) => {
    await addImage(formData);
    setAddDialogOpen(false);
  };

  const handleCrop = async (id: string, area: CropArea) => {
    const croppedImage = await cropImage(id, area);
    setImageToEdit(croppedImage);
  };

  const handleToggleImageSelection = (image: MediaImage) => {
    if (selectedImages?.find((i) => i.id === image.id)) {
      setSelectedImages((images) => (images ?? []).filter((i) => i.id !== image.id));
    } else {
      setSelectedImages((images) => [...(images ?? []), image]);
    }
  };

  const isAdmin = context?.role === "ADMIN";
  return (
    <Stack
      sx={{
        height: "100%",
        marginTop: 1,
        paddingBottom: 1,
      }}
    >
      <ImagesPreview
        isLoading={isLoading}
        images={images}
        selectedImages={selectedImages}
        search={searchCriteria.text}
        onSearchChange={(text) => setSearchCriteria({ text, tags: searchCriteria.tags })}
        onToggleImageSelection={handleToggleImageSelection}
        onDeleteImage={isAdmin ? setConfirmDialogOpen : undefined}
        onEditImage={isAdmin ? setImageToEdit : undefined}
        onCropImage={isAdmin ? setImageToCrop : undefined}
        onUpdatePrivacy={isAdmin ? updateImagePrivacy : undefined}
      />
      <Actions alignToBottom>
        <DownloadImages
          selectedImages={selectedImages}
          onStartSelection={() => setSelectedImages([])}
          onClearSelection={() => setSelectedImages(null)}
        />
        {isAdmin && <AddFab label="Dodaj grafikę" onClick={() => setAddDialogOpen(true)} />}
      </Actions>
      <AddImageDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} onNewFile={handleNewFile} />
      <EditImageDialog
        open={!!imageToEdit}
        image={imageToEdit}
        onEdit={async (id, data) => {
          await updateImage(id, data);
        }}
        onClose={() => setImageToEdit(null)}
      />
      <CropImageDialog image={imageToCrop} onCrop={handleCrop} onClose={() => setImageToCrop(null)} />
      <ConfirmationDialog
        title="Usuń grafikę"
        content="Czy na pewno chcesz usunąć grafikę?"
        open={!!confirmDialogOpen}
        onCancel={() => setConfirmDialogOpen(null)}
        onConfirm={async () => {
          if (!confirmDialogOpen) {
            return;
          }
          await deleteImage(confirmDialogOpen);
          setConfirmDialogOpen(null);
        }}
      />
    </Stack>
  );
};

export default MediaBank;
